import { TreeListItem } from '../types/types';
import { backendApiService } from './backendApiService';

// Cache time in milliseconds (5 minutes)
const CACHE_TIME = 5 * 60 * 1000;

// Storage keys
const TREE_LIST_CACHE_KEY = 'tree_list_cache';
const TREE_LIST_CACHE_TIMESTAMP_KEY = 'tree_list_cache_timestamp';

/**
 * Service for managing the list of trees.
 * This is separated from the tree editing functionality.
 * 
 * Features:
 * - Caches the tree list in localStorage for performance
 * - Refreshes cache when needed
 * - Manages tree list operations
 */
class TreeListService {
  /**
   * Fetch all trees available to the user
   * Uses cached data if available and not expired
   */
  async fetchTreeList(forceRefresh = false): Promise<TreeListItem[]> {
    // Check if we should use cached data
    if (!forceRefresh) {
      const cachedList = this.getCachedTreeList();
      if (cachedList) {
        console.log('Using cached tree list');
        return cachedList;
      }
    }
    
    try {
      // Fetch fresh data from backend
      const treeList = await backendApiService.fetchTreeList();
      // Cache the result
      this.cacheTreeList(treeList);
      
      return treeList;
    } catch (error) {
      console.error('Failed to fetch tree list:', error);
      
      // If fetch fails, try to return cached data even if expired
      const cachedList = this.getCachedTreeList(true);
      if (cachedList) {
        console.warn('Using expired cache due to fetch failure');
        return cachedList;
      }
      
      throw error;
    }
  }
  
  /**
   * Get cached tree list if available and not expired
   */
  private getCachedTreeList(ignoreExpiry = false): TreeListItem[] | null {
    try {
      const cachedDataJson = localStorage.getItem(TREE_LIST_CACHE_KEY);
      const timestampStr = localStorage.getItem(TREE_LIST_CACHE_TIMESTAMP_KEY);
      
      if (!cachedDataJson || !timestampStr) {
        return null;
      }
      
      // Check if cache is expired
      if (!ignoreExpiry) {
        const timestamp = parseInt(timestampStr, 10);
        const now = Date.now();
        
        if (now - timestamp > CACHE_TIME) {
          console.log('Tree list cache expired');
          return null;
        }
      }
      
      return JSON.parse(cachedDataJson);
    } catch (error) {
      console.error('Error reading tree list cache:', error);
      return null;
    }
  }
  
  /**
   * Cache the tree list in localStorage
   */
  private cacheTreeList(treeList: TreeListItem[]): void {
    try {
      localStorage.setItem(TREE_LIST_CACHE_KEY, JSON.stringify(treeList));
      localStorage.setItem(TREE_LIST_CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Failed to cache tree list:', error);
    }
  }
  
  /**
   * Clear the tree list cache
   */
  clearCache(): void {
    try {
      localStorage.removeItem(TREE_LIST_CACHE_KEY);
      localStorage.removeItem(TREE_LIST_CACHE_TIMESTAMP_KEY);
    } catch (error) {
      console.error('Failed to clear tree list cache:', error);
    }
  }
  
  /**
   * Refresh the cache with fresh data from backend
   */
  async refreshCache(): Promise<TreeListItem[]> {
    return this.fetchTreeList(true);
  }
}

// Export a singleton instance
export const treeListService = new TreeListService();